import { useAsclepius } from "@/composables/api/asclepius"
import { reactive, ref, watch } from "vue"
import {
  Availability,
  AvailabilityApiListAvailabilitiesCalendarRequest,
  AvailabilityApiListAvailabilitiesRequest,
  CalendarAvailability,
  CreateAvailabilitiesRequest,
  CreateAvailabilitiesResponse,
  ListAvailabilitiesCalendarResponse,
  ListAvailabilitiesResponse,
  UpdateAvailabilityBlockedStatusResponse
} from "@vitable/asclepius-api-client-ts"

export const useListAvailabilities = () => {
  const { getAvailabilityApi, call, callResult } = useAsclepius()

  const availabilities = reactive<Paginated<Availability>>({
    currentPage: 1,
    nextPage: undefined,
    prevPage: undefined,
    totalCount: 0,
    results: []
  })

  async function listAvailabilities(request: AvailabilityApiListAvailabilitiesRequest) {
    await call(() => getAvailabilityApi().listAvailabilities(request))
  }

  watch(callResult, (newValue) => {
    if (newValue.isSuccess()) {
      const data: ListAvailabilitiesResponse = callResult.value.getData()
      availabilities.results = data.results
      availabilities.nextPage = data.next
      availabilities.prevPage = data.previous
      availabilities.totalCount = data.count
    }
  })

  return {
    listAvailabilities,
    listAvailabilitiesResult: callResult,
    availabilities
  }
}

export const useListAvailabilitiesCalendar = () => {
  const { getAvailabilityApi, call, callResult } = useAsclepius()
  const calendarAvailabilities = ref<Array<CalendarAvailability>>([])

  async function listAvailabilitiesCalendar(request: AvailabilityApiListAvailabilitiesCalendarRequest) {
    await call(() => getAvailabilityApi().listAvailabilitiesCalendar(request))
  }

  watch(callResult, (newValue) => {
    if (newValue.isSuccess()) {
      const data: ListAvailabilitiesCalendarResponse = callResult.value.getData()
      calendarAvailabilities.value = data.availabilities
    }
  })

  return {
    listAvailabilitiesCalendar,
    listAvailabilitiesCalendarResult: callResult,
    calendarAvailabilities
  }
}

/**
 * Creates a new Availability with given properties / constraints.
 */
export function useCreateAvailabilities() {
  const { getAvailabilityApi, call, callResult } = useAsclepius()

  const createdAvailabilities = ref<Array<Availability>>([])

  async function createAvailabilities(request: CreateAvailabilitiesRequest) {
    await call(() =>
      getAvailabilityApi().createAvailabilities({
        createAvailabilitiesRequest: request
      })
    )
  }

  watch(callResult, (newValue) => {
    if (newValue.isSuccess()) {
      const data: CreateAvailabilitiesResponse = callResult.value.getData()
      createdAvailabilities.value = data.availabilities
    }
  })

  return {
    createAvailabilities,
    createAvailabilitiesResult: callResult,
    createdAvailabilities
  }
}

export function useDeleteAvailabilities() {
  const { getAvailabilityApi, call, callResult } = useAsclepius()

  async function deleteAvailability(providerId: string, availabilityId: string) {
    await call(() =>
      getAvailabilityApi().deleteAvailability({
        availabilityId,
        providerId
      })
    )
  }

  return {
    deleteAvailability,
    deleteAvailabilityResult: callResult
  }
}

export function useUpdateAvailabilityBlockedStatus() {
  const { getAvailabilityApi, call, callResult } = useAsclepius()

  const updatedAvailability = ref<Availability>()

  async function updateAvailabilityBlockedStatus(availabilityId: string, blockedIn: boolean, blockReason: string) {
    await call(() =>
      getAvailabilityApi().updateAvailabilityBlockedStatus({
        availabilityId,
        updateAvailabilityBlockedStatusRequest: {
          blocked_in: blockedIn,
          block_reason: blockReason
        }
      })
    )
  }

  watch(callResult, (newValue) => {
    if (newValue.isSuccess()) {
      const data: UpdateAvailabilityBlockedStatusResponse = callResult.value.getData()
      updatedAvailability.value = data.availability
    }
  })

  return {
    updateAvailabilityBlockedStatus,
    updateAvailabilityBlockedStatusResult: callResult,
    updatedAvailability
  }
}
